import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import ASICVerificationBanner from "../../../assets/technologies/tools-and-languages/uvm/uvmbannerimage.png";
import ProfileImage from "../../../assets/blogpages/person.png";
import "./ASICVerification.css";
import { AnimatePresence, motion } from "framer-motion";

// Simple highlight component: wraps matches in <mark>
const Highlight = ({ text = "", query = "" }) => {
  if (!query) return <>{text}</>;
  const parts = text.split(new RegExp(`(${query})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase() ? (
      <mark key={i}>{part}</mark>
    ) : (
      part
    )
  );
};

const ASICVerification = () => {
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const closeOverlay = () => setShowPopup(false);

  const [replyVisible, setReplyVisible] = useState({});
  const toggleReply = (id) =>
    setReplyVisible(prev => ({ ...prev, [id]: !prev[id] }));

  // refs for scrolling to each section
  const sectionRefs = {
    intro: useRef(null),
    uvm: useRef(null),
    coverage: useRef(null),
    formal: useRef(null),
    emulation: useRef(null),
    lowpower: useRef(null),
    security: useRef(null),
    dft: useRef(null),
    metrics: useRef(null),
    debug: useRef(null),
    trends: useRef(null),
    comments: useRef(null),
  };

  const scrollTo = ref =>
    ref.current && ref.current.scrollIntoView({ behavior: "smooth", block: "center" });

  const handleSearch = () => setSearchTerm(searchInput.trim());

  useEffect(() => {
    if (!searchTerm) return;
    const firstMark = document.querySelector("mark");
    firstMark?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [searchTerm]);

  // fade-in/out observer
  useEffect(() => {
    const io = new IntersectionObserver(
      entries =>
        entries.forEach(e =>
          e.isIntersecting
            ? e.target.classList.add("visible")
            : e.target.classList.remove("visible")
        ),
      { threshold: 0.1 }
    );
    document.querySelectorAll(".fade-section").forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  // auto-dismiss popup
  useEffect(() => {
    if (showPopup) {
      const t = setTimeout(() => setShowPopup(false), 2000);
      return () => clearTimeout(t);
    }
  }, [showPopup]);

  const handleCommentSubmit = e => {
    e.preventDefault();
    setShowPopup(true);
    e.target.reset();
  };
  const handleReplySubmit = e => {
    e.preventDefault();
    setShowPopup(true);
    e.target.reset();
  };

  return (
    <div className="blog-post-page asic-verification">
      {/* Banner */}
      <div className="blog-banner fade-section">
        <div className="banner-overlay">
          <h1>ASIC Verification</h1>
          <div className="breadcrumb">
            <Link to="/services">Services</Link> | ASIC Verification
          </div>
        </div>
      </div>

      {/* Mobile nav */}
      <div className="mobile-nav fade-section">
        <div className="search-box">
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            type="text"
            placeholder="Search keyword..."
            className="search-input"
          />
          <button onClick={handleSearch} className="search-button">
            Search
          </button>
        </div>
        <div className="table-of-contents">
          <h4>In This Article</h4>
          <ul>
            {Object.entries(sectionRefs).map(([key, ref]) => (
              <li key={key}>
                <button onClick={() => scrollTo(ref)}>
                  {{
                    intro: "Introduction",
                    uvm: "UVM Methodology",
                    coverage: "Functional Coverage",
                    formal: "Formal Verification",
                    emulation: "Emulation & Acceleration",
                    lowpower: "Power-Aware Verification",
                    security: "Security Verification",
                    dft: "DFT & Scan Coverage",
                    metrics: "Metrics & Reporting",
                    debug: "Debug & Diagnostics",
                    trends: "Future Trends",
                    comments: "Comments",
                  }[key]}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="blog-content-container">
        {/* Left Column */}
        <div className="blog-left-column">
          <img
            className="post-main-image fade-section"
            src={ASICVerificationBanner}
            alt="ASIC Verification"
          />

          <div className="post-info-row fade-section">
            <span className="post-author">By Jane Doe</span>
            <span className="post-category">Category: Verification</span>
            <button className="comment-link" onClick={() => scrollTo(sectionRefs.comments)}>
              2 Comments
            </button>
          </div>

          {/* 1. Introduction */}
          <h2 ref={sectionRefs.intro} className="fade-section">
            Introduction to ASIC Verification
          </h2>
          <p className="fade-section">
            <Highlight
              text="ASIC verification ensures that a custom silicon design functions correctly under all intended operating conditions before tape-out. This critical phase typically consumes more than half of the total project effort and budget."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Verification covers functional correctness, timing robustness, power intent, and security properties through a combination of simulation, formal methods, hardware emulation, and post-silicon validation."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="A structured verification plan outlines testbench architecture, coverage goals, regression strategies, and performance targets to systematically uncover design issues and ensure silicon reliability."
              query={searchTerm}
            />
          </p>

          {/* 2. UVM Methodology */}
          <h2 ref={sectionRefs.uvm} className="fade-section">
            UVM-Based Verification Methodology
          </h2>
          <p className="fade-section">
            <Highlight
              text="Universal Verification Methodology (UVM) provides a standardized class library and transaction-level modelling framework for building modular, reusable, and scalable verification environments."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Key UVM components include sequence items, drivers, monitors, agents, scoreboards, and environments—allowing verification engineers to construct directed and constrained-random tests with ease."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Using UVM factory overrides and configuration objects, teams can rapidly swap components, tune stimulus parameters, and share verification IP across multiple projects for faster development."
              query={searchTerm}
            />
          </p>

          {/* 3. Functional Coverage */}
          <h2 ref={sectionRefs.coverage} className="fade-section">
            Functional Coverage & Coverage-Driven Verification
          </h2>
          <p className="fade-section">
            <Highlight
              text="Functional coverage metrics track which design features and corner cases have been exercised by the test suite. Covergroups, coverpoints, and crosses provide quantitative visibility into verification completeness."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Coverage-driven test generation leverages feedback loops to automatically generate stimuli that target untested states or transitions, significantly improving defect discovery efficiency."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Code coverage (line, toggle, expression) complements functional coverage, ensuring that all logical branches and conditions in the RTL are validated before sign-off."
              query={searchTerm}
            />
          </p>

          {/* 4. Formal Verification */}
          <h2 ref={sectionRefs.formal} className="fade-section">
            Formal Verification Techniques
          </h2>
          <p className="fade-section">
            <Highlight
              text="Formal verification uses mathematical proofs to exhaustively check properties such as protocol compliance, deadlock freedom, and data integrity, finding corner-case bugs that simulation may miss."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Assertion-based verification embeds SystemVerilog Assertions (SVA) in the RTL or testbench to specify properties, which formal engines then prove or find counterexamples for."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Equivalence checking compares RTL against a reference model (e.g., High-Level Synthesis output), ensuring that synthesis and optimization steps preserve intended functionality."
              query={searchTerm}
            />
          </p>

          {/* 5. Emulation & Simulation Acceleration */}
          <h2 ref={sectionRefs.emulation} className="fade-section">
            Emulation & Hardware Acceleration
          </h2>
          <p className="fade-section">
            <Highlight
              text="Hardware emulation platforms map the design onto FPGAs or custom emulators, providing orders-of-magnitude speedup over software simulation for large regression suites and system-level tests."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Simulation acceleration uses hybrid emulation/simulation flows, offloading RTL simulation to hardware while preserving testbench flexibility in software, enabling comprehensive SoC-level validation."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Co-simulation with software drivers and firmware brings up real use cases early, uncovering integration bugs that pure RTL simulation cannot detect."
              query={searchTerm}
            />
          </p>

          {/* 6. Power-Aware Verification */}
          <h2 ref={sectionRefs.lowpower} className="fade-section">
            Power-Aware & Low-Power Verification
          </h2>
          <p className="fade-section">
            <Highlight
              text="Verifying power intent ensures that UPF or CPF annotations are correctly implemented. Simulation exercises power-state transitions, isolation, retention, and gating sequences to catch low-power bugs."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Coverage metrics track power mode transitions and retention scenarios, validating correct behavior when blocks enter or exit low-power states."
              query={searchTerm}
            />
          </p>

          {/* 7. Security Verification */}
          <h2 ref={sectionRefs.security} className="fade-section">
            Security & Side-Channel Verification
          </h2>
          <p className="fade-section">
            <Highlight
              text="Security verification checks cryptographic modules, access controls, and side-channel resilience. Testbenches exercise tamper scenarios, fault injection, and power analysis patterns to validate secure operation."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Formal property checks can prove absence of information leakage under unintended input combinations, providing high assurance for safety-critical and financial applications."
              query={searchTerm}
            />
          </p>

          {/* 8. DFT & Scan Coverage */}
          <h2 ref={sectionRefs.dft} className="fade-section">
            DFT & Scan Chain Verification
          </h2>
          <p className="fade-section">
            <Highlight
              text="Design-for-test (DFT) features such as scan chains, BIST, and boundary scan are verified in simulation to ensure testability goals and fault coverage metrics are met."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Fault simulation and ATPG validation verify that the inserted scan logic achieves target coverage for stuck-at, bridging, and at-speed delay faults."
              query={searchTerm}
            />
          </p>

          {/* 9. Metrics & Reporting */}
          <h2 ref={sectionRefs.metrics} className="fade-section">
            Verification Metrics & Reporting
          </h2>
          <p className="fade-section">
            <Highlight
              text="Tracking regression runtime, coverage closure, bug discovery rate, and resource utilization provides management visibility into verification health and project risk."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Automated dashboards collate results across nightly builds, highlighting coverage holes and newly discovered issues to guide test plan improvements."
              query={searchTerm}
            />
          </p>

          {/* 10. Debug & Diagnostics */}
          <h2 ref={sectionRefs.debug} className="fade-section">
            Debug & Diagnostics
          </h2>
          <p className="fade-section">
            <Highlight
              text="Advanced waveform viewers, transaction loggers, and built-in UVM reporting help pinpoint root causes of failures. Coverage holes direct debug focus to untested scenarios."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Hardware-assisted debug via emulation trace buffers and logic analyzers provides system-level visibility post-silicon for in-field diagnostics."
              query={searchTerm}
            />
          </p>

          {/* 11. Future Trends */}
          <h2 ref={sectionRefs.trends} className="fade-section">
            Future Trends in Verification
          </h2>
          <p className="fade-section">
            <Highlight
              text="AI-driven test generation will analyze past regressions to craft targeted stimulus for untested functionality, reducing manual test creation effort."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Cloud-based verification farms and distributed acceleration will enable massive parallel regressions, shrinking end-to-end turn-around times for large SoCs."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Standardized verification metadata schemas will improve reuse of testbenches, coverage models, and metrics across IP and SoC projects."
              query={searchTerm}
            />
          </p>

          {/* Comment Section */}
          <div className="comment-section" ref={sectionRefs.comments}>
            <h3>2 Comments</h3>

            {/* Comment 1 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img src={ProfileImage} alt="User" className="comment-profile-img" />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">Eva Verify</span>
                    <span className="comment-date">April 18, 2025</span>
                  </div>
                </div>
              </div>
              <p className="comment-text">
                Great overview on UVM best practices—very helpful for new projects!
              </p>
              <button className="reply-button" onClick={() => toggleReply("eva")}>
                Reply
              </button>
              {replyVisible["eva"] && (
                <div className="leave-comment">
                  <h3>Reply to Eva Verify</h3>
                  <form onSubmit={handleReplySubmit}>
                    <div className="form-group">
                      <label>Name</label>
                      <input type="text" placeholder="Your name..." required />
                    </div>
                    <div className="form-group">
                      <label>Message</label>
                      <textarea placeholder="Your reply..." required />
                    </div>
                    <button type="submit" className="send-message-btn">
                      Reply
                    </button>
                  </form>
                </div>
              )}
            </div>

            {/* Comment 2 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img src={ProfileImage} alt="User" className="comment-profile-img" />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">Frank Formal</span>
                    <span className="comment-date">April 19, 2025</span>
                  </div>
                </div>
              </div>
              <p className="comment-text">
                The formal section clarified a lot of concepts—thank you!
              </p>
              <button className="reply-button" onClick={() => toggleReply("frankf")}>
                Reply
              </button>
              {replyVisible["frankf"] && (
                <div className="leave-comment">
                  <h3>Reply to Frank Formal</h3>
                  <form onSubmit={handleReplySubmit}>
                    <div className="form-group">
                      <label>Name</label>
                      <input type="text" placeholder="Your name..." required />
                    </div>
                    <div className="form-group">
                      <label>Message</label>
                      <textarea placeholder="Your reply..." required />
                    </div>
                    <button type="submit" className="send-message-btn">
                      Reply
                    </button>
                  </form>
                </div>
              )}
            </div>

            {/* Leave a comment */}
            <div className="leave-comment fade-section">
              <h3>Leave a comment</h3>
              <p>Have questions or feedback? Fill out the form below!</p>
              <form onSubmit={handleCommentSubmit}>
                <div className="form-group">
                  <label>Name</label>
                  <input type="text" placeholder="Name here..." required />
                </div>
                <div className="form-group">
                  <label>Message</label>
                  <textarea placeholder="Message here..." required />
                </div>
                <button type="submit" className="send-message-btn">
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>

        {/* Desktop Sidebar */}
        <div className="blog-right-column fade-section">
          <div className="search-box">
            <input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              type="text"
              placeholder="Search keyword..."
              className="search-input"
            />
            <button onClick={handleSearch} className="search-button">
              Search
            </button>
          </div>
          <div className="table-of-contents">
            <h4>In This Article</h4>
            <ul>
              {Object.entries(sectionRefs).map(([key, ref]) => (
                <li key={key}>
                  <button onClick={() => scrollTo(ref)}>
                    {{
                      intro: "Introduction",
                      uvm: "UVM Methodology",
                      coverage: "Functional Coverage",
                      formal: "Formal Verification",
                      emulation: "Emulation & Acceleration",
                      lowpower: "Power-Aware Verification",
                      security: "Security Verification",
                      dft: "DFT & Scan Coverage",
                      metrics: "Metrics & Reporting",
                      debug: "Debug & Diagnostics",
                      trends: "Future Trends",
                      comments: "Comments",
                    }[key]}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Thank You Popup */}
        <AnimatePresence>
          {showPopup && (
            <motion.div
              className="thank-you-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeOverlay}
            >
              <motion.div
                className="thank-you-content"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                transition={{ type: "spring", stiffness: 300 }}
                onClick={e => e.stopPropagation()}
              >
                <div className="checkmark-container">
                  <motion.svg
                    className="checkmark"
                    viewBox="0 0 52 52"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1, ease: "easeInOut" }}
                  >
                    <circle cx="26" cy="26" r="25" stroke="#007bff" strokeWidth="2" />
                    <path
                      d="M16 27.5L23 34.5L36 19.5"
                      stroke="#007bff"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </motion.svg>
                </div>
                <h3>Thank you for the feedback!</h3>
                <p>We appreciate your comment.</p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default ASICVerification;